<template>
    <q-layout view="hHh LpR fFf">
        <!--
            Header. Quasar's q-header positions itself fixed at top:0 with
            z-index ~2000, so any sibling laid out BEFORE the header inside
            q-layout (the previous SANDBOX MODE q-bar placement) ends up
            visually covered by the header. The fix is to nest the q-bar
            INSIDE the q-header so Quasar's layout engine reserves space for
            BOTH the banner row AND the toolbar row, and so the q-page-
            container offset accounts for the combined height.
        -->
        <q-header class="bg-white text-dark" style="border-bottom: 1px solid #E2E8F0;">
            <!--
                SANDBOX MODE banner. Sandbox-tenant-mode.md v8 rule 21:
                "Portal renders a persistent red 'SANDBOX MODE' banner across
                the entire UI shell whenever the resolved tenant has
                IsSandbox = 1. Cannot be dismissed."

                Nested inside q-header (above the toolbar) so the page content
                shifts down to clear it (a sibling-of-q-header q-bar gets
                visually covered by q-header's position:fixed).
            -->
            <q-bar v-if="store.isSandbox" class="bg-red-9 text-white q-px-md" style="min-height: 36px;">
                <q-icon name="science" size="20px" class="q-mr-sm" />
                <div class="text-weight-bold" style="letter-spacing: 0.5px;">SANDBOX MODE</div>
                <div class="text-caption q-ml-md" style="opacity: 0.9;">
                    You are signed into a test-mode sandbox. Signed documents are not legally binding.
                </div>
                <q-space />
                <!--
                    JS-driven form-POST (same pattern as services/authActions.js
                    logout). q-btn type="submit" inside a static <form> wasn't
                    reliably triggering native form submission inside the Quasar
                    component tree - the click was navigating to the form action
                    as a GET and producing a 405/404 surface.
                -->
                <q-btn
                    flat
                    dense
                    no-caps
                    color="white"
                    icon="exit_to_app"
                    label="Exit Sandbox"
                    class="text-weight-medium"
                    aria-label="Exit sandbox and return to live"
                    @click="exitSandbox" />
            </q-bar>

            <!--
                EV-1 + UI-1: "Please verify your email" banner. Renders
                across all authenticated pages when ApplicationUser.
                EmailConfirmed = false. Cannot be dismissed inline (the
                user must either click the confirmation link in the
                email OR update to a new email address). GATE-1 will
                eventually refuse package sends from unverified users,
                so the banner serves as the in-product remediation
                path. Nested inside q-header for the same reason as the
                SANDBOX bar — a sibling-of-q-header q-bar gets visually
                covered by q-header's position:fixed.
            -->
            <div
                v-if="!store.emailConfirmed"
                class="email-verify-banner row items-center no-wrap q-px-md">
                <q-icon name="mark_email_unread" size="22px" class="q-mr-sm" />
                <div class="email-verify-text ellipsis">
                    <span class="text-weight-bold">Verify your email address.</span>
                    <span class="email-verify-detail q-ml-xs">We sent a confirmation link to <strong>{{ store.userEmail }}</strong>. Click it to start sending packages.</span>
                </div>
                <q-space />
                <q-btn
                    flat
                    dense
                    no-caps
                    icon="forward_to_inbox"
                    :label="resendCooldown > 0 ? `Resend (${resendCooldown}s)` : 'Resend email'"
                    class="email-verify-btn q-ml-md"
                    :disable="resending || resendCooldown > 0"
                    :loading="resending"
                    @click="resendEmailConfirmation" />
                <q-btn
                    flat
                    dense
                    no-caps
                    icon="edit"
                    label="Update email"
                    class="email-verify-btn q-ml-sm"
                    tag="a"
                    href="/Account/MyAccount" />
            </div>
            <q-toolbar>
                <q-btn flat dense round icon="menu" color="grey-7" @click="store.toggleDrawer()" />
                <a href="/Dashboard" class="row items-center text-no-decoration">
                    <img src="/img/logo-header.png" alt="eSign Launchpad" style="height: 36px;" />
                </a>
                <q-space />
                <q-btn flat round icon="person" color="grey-7">
                    <q-menu>
                        <q-list style="min-width: 200px">
                            <q-item-label header>{{ store.userName }}</q-item-label>
                            <!--
                                Sandbox: substitute the synthesized paired-user email
                                (kai+sandbox-00000127@... etc.) with a clear "Sandbox Account"
                                label. The synthesized address is implementation detail - the
                                user logged in via the magic link from their live session and
                                doesn't need to be reminded of the per-sandbox alias every
                                time they open the avatar menu.
                            -->
                            <q-item-label caption class="q-px-md">
                                {{ store.isSandbox ? 'Sandbox Account' : store.userEmail }}
                            </q-item-label>
                            <q-separator />
                            <q-item clickable v-close-popup tag="a" href="/Account/MyAccount">
                                <q-item-section avatar><q-icon name="manage_accounts" /></q-item-section>
                                <q-item-section>My Account</q-item-section>
                            </q-item>
                            <q-separator />
                            <!--
                                Sandbox: replace Logout with Exit Sandbox. The user isn't
                                "logged in" to the sandbox as a normal account - they're
                                impersonating via the consumed magic-link cookie. The
                                semantic action to leave is "exit back to live", not "log
                                out". If they need to fully log out, they exit sandbox and
                                then log out from the live session.
                            -->
                            <q-item
                                v-if="store.isSandbox"
                                clickable
                                v-close-popup
                                @click="exitSandbox">
                                <q-item-section avatar><q-icon name="exit_to_app" /></q-item-section>
                                <q-item-section>Exit Sandbox</q-item-section>
                            </q-item>
                            <q-item
                                v-else
                                clickable
                                v-close-popup
                                @click="store.logout()">
                                <q-item-section avatar><q-icon name="logout" /></q-item-section>
                                <q-item-section>Logout</q-item-section>
                            </q-item>
                        </q-list>
                    </q-menu>
                </q-btn>
            </q-toolbar>
        </q-header>

        <!-- Sidebar Drawer. Width matches the public-mode GuideSidebar (260px) so the Dashboard
             nav and the in-content docs nav feel like one consistent sidebar across the app. -->
        <q-drawer v-model="store.drawerOpen" show-if-above bordered :width="260">
            <AppSidebar />
        </q-drawer>

        <!-- Main Content -->
        <q-page-container>
            <q-page padding>
                <slot></slot>
            </q-page>
        </q-page-container>

        <!-- Global upgrade-prompt dialog. Mounted once here so any page can
             call useUpgradePromptStore().guard(...) without local mount. -->
        <UpgradePromptDialog />
    </q-layout>
</template>

<script>
import { useAsideStore } from '@stores/aside.js';
import { setTenantTimeZone } from '../../services/dateFormat';
import { branding } from '../../services/signApi';
import api from '@services/api.js';
import AppSidebar from './AppSidebar.vue';
import UpgradePromptDialog from './UpgradePromptDialog.vue';

export default {
    name: 'AppLayout',

    components: {
        AppSidebar,
        UpgradePromptDialog
    },

    props: {
        /**
         * The viewkey for the current page (e.g., 'settings_users')
         * Used to highlight the active menu item
         */
        viewkey: {
            type: String,
            required: true
        }
    },

    data() {
        return {
            // EV-1 + UI-1: banner state.
            resending: false,
            resendCooldown: 0,
            resendTimer: null
        };
    },

    beforeUnmount() {
        if (this.resendTimer) {
            clearInterval(this.resendTimer);
            this.resendTimer = null;
        }
    },

    setup(props) {
        const store = useAsideStore();

        // Initialize from page config if available
        const config = window._pageConfig || {};
        store.initFromConfig(config);

        // Set current view for active menu highlighting
        store.setCurrentView(props.viewkey);

        return { store };
    },

    computed: {
        /**
         * Antiforgery token harvested from the page's hidden form. Used by the
         * SANDBOX MODE banner's Exit Sandbox button so the POST passes ASP.NET
         * Core's [ValidateAntiForgeryToken] gate on /sandbox/return-to-live.
         * Matches the same harvesting pattern services/authActions.js uses for
         * the logout flow.
         */
        antiforgeryToken() {
            return document.querySelector('input[name="__RequestVerificationToken"]')?.value || '';
        }
    },

    // Watch viewkey so pages that change it reactively (e.g. IntegrationGuides switching between
    // topics via ?topic= query param) keep the sidebar active-item highlight in sync. Without this,
    // setup() only ran once with the initial viewkey and subsequent changes were ignored.
    watch: {
        viewkey(newViewkey) {
            this.store.setCurrentView(newViewkey);
        }
    },

    mounted() {
        this.initTenantTimeZone();
    },

    // SINGLE methods block. A prior refactor introduced TWO `methods: {...}`
    // entries; in a JS object literal duplicate keys mean the later property
    // silently replaces the earlier one, which dropped `exitSandbox` from
    // the component instance entirely and made every "Exit Sandbox" button
    // a no-op. Keep this block consolidated — adding a second `methods:`
    // section anywhere else in this file will silently re-introduce the
    // same bug.
    methods: {
        /**
         * Exit the sandbox via JS-driven form POST to /sandbox/return-to-live.
         * Same pattern as services/authActions.js: avoids the q-btn
         * type="submit" + static form interaction that was producing a
         * GET navigation to the action URL instead of a POST submission.
         */
        exitSandbox() {
            const form = document.createElement('form');
            form.method = 'POST';
            form.action = '/sandbox/return-to-live';
            form.style.display = 'none';

            const tokenInput = document.createElement('input');
            tokenInput.type = 'hidden';
            tokenInput.name = '__RequestVerificationToken';
            tokenInput.value = this.antiforgeryToken;
            form.appendChild(tokenInput);

            document.body.appendChild(form);
            form.submit();
        },

        async initTenantTimeZone() {
            try {
                const response = await branding.get();
                const data = response.data || {};
                setTenantTimeZone(data.timeZoneId || null);
            } catch {
                // Branding not available - leave timezone as UTC default
            }
        },

        /**
         * EV-1 + UI-1: Resend confirmation-email button handler.
         * Hits POST /api/v1/account/profile/resend-email-confirmation,
         * starts the cooldown clock on success so repeated clicks
         * within 60s don't trigger more sends, and notifies the user.
         */
        async resendEmailConfirmation() {
            if (this.resending || this.resendCooldown > 0) return;
            this.resending = true;
            try {
                const response = await api.post('/account/profile/resend-email-confirmation');
                const cooldown = (response.data && response.data.cooldownRemainingSeconds) || 60;
                this.startResendCooldown(cooldown);
                const masked = (response.data && response.data.maskedEmail) || this.store.userEmail;
                this.$q.notify({
                    type: 'positive',
                    message: `Confirmation email sent to ${masked}. Check your inbox (and spam folder).`,
                    timeout: 6000
                });
            } catch (err) {
                // ResendCooldown server response includes the remaining
                // seconds in the body — surface it so the UI can start
                // the timer from the server's perspective.
                const serverCooldown = err?.response?.data?.cooldownRemainingSeconds;
                if (serverCooldown && serverCooldown > 0) {
                    this.startResendCooldown(serverCooldown);
                }
                this.$q.notify({
                    type: 'negative',
                    message: err.displayMessage || err?.response?.data?.message ||
                        'Could not send the confirmation email. Please try again in a moment.'
                });
            } finally {
                this.resending = false;
            }
        },

        startResendCooldown(seconds) {
            this.resendCooldown = seconds;
            if (this.resendTimer) clearInterval(this.resendTimer);
            this.resendTimer = setInterval(() => {
                this.resendCooldown -= 1;
                if (this.resendCooldown <= 0) {
                    clearInterval(this.resendTimer);
                    this.resendTimer = null;
                    this.resendCooldown = 0;
                }
            }, 1000);
        }
    }
};
</script>

<!--
    Global card shadow. AppLayout wraps every back-office page, so an unscoped
    style here cascades to all q-card instances across the portal without having
    to edit each .vue file individually.

    ALL q-cards get a generous shadow - the `flat` prop is never used in this
    project by policy. If you need a non-elevated container (e.g., nested inside
    another card), use a plain <div> or <q-card-section>, not <q-card flat>.
-->
<style>
.q-card {
    box-shadow:
        0 4px 16px rgba(15, 23, 42, 0.08),
        0 8px 32px rgba(15, 23, 42, 0.06) !important;
}

/*
    EV-1 / UI-1 "Verify your email" banner. Built as a plain flex banner
    (NOT a q-bar - q-bar is a window-title-bar widget that dims child text
    and forces a 36px height, which washed out the message and wrapped the
    title). Deep amber with full-opacity white text for strong contrast;
    single-line with ellipsis so it never wraps awkwardly; buttons rendered
    as outlined white chips so they read clearly as actions.
*/
.email-verify-banner {
    background: #B45309;
    color: #ffffff;
    min-height: 46px;
    padding-top: 8px;
    padding-bottom: 8px;
    font-size: 13.5px;
    line-height: 1.35;
}
.email-verify-banner .email-verify-text {
    min-width: 0;
}
.email-verify-banner .email-verify-detail {
    color: rgba(255, 255, 255, 0.92);
    font-weight: 400;
}
.email-verify-banner strong {
    font-weight: 600;
}
.email-verify-banner .q-btn.email-verify-btn {
    color: #ffffff;
    border: 1px solid rgba(255, 255, 255, 0.6);
    border-radius: 6px;
    padding: 2px 12px;
    font-size: 12.5px;
    white-space: nowrap;
}
.email-verify-banner .q-btn.email-verify-btn:hover {
    background: rgba(255, 255, 255, 0.14);
}
.email-verify-banner .q-btn.email-verify-btn.disabled {
    opacity: 0.6 !important;
}
</style>
